import { DatePipe } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, computed, inject, signal } from '@angular/core';

import {
  BadgeComponent, ButtonComponent, CardComponent, ConfirmDialogComponent,
  EmptyStateComponent, SpinnerComponent, ToastService,
} from '@echospaces-web-monorepo/es-shared-ui';

import { UpgradeService, type CustomerUpgrade, type UpgradeStatus } from './upgrade.service';

const STATUS_VARIANT: Record<UpgradeStatus, 'primary' | 'secondary' | 'success' | 'warning' | 'danger'> = {
  PENDING:     'warning',
  APPROVED:    'primary',
  RUNNING:     'primary',
  COMPLETED:   'success',
  FAILED:      'danger',
  ROLLED_BACK: 'secondary',
};

const STATUS_LABEL: Record<UpgradeStatus, string> = {
  PENDING:     'pending',
  APPROVED:    'approved · waiting for orchestrator',
  RUNNING:     'running',
  COMPLETED:   'completed',
  FAILED:      'failed',
  ROLLED_BACK: 'rolled back',
};

/**
 * Schema-upgrade inbox + lifecycle view. Three sections:
 *
 *   1. **In-flight** — APPROVED + RUNNING. Auto-polled every 5s by the
 *      `UpgradeService`.
 *   2. **Pending** — needs the customer Super-Admin's approval.
 *   3. **History** — COMPLETED, FAILED (with errorMessage), ROLLED_BACK.
 *      Collapsed by default; expand on demand.
 */
@Component({
  selector: 'es-upgrades-panel',
  imports: [
    DatePipe, BadgeComponent, ButtonComponent, CardComponent,
    SpinnerComponent, EmptyStateComponent, ConfirmDialogComponent,
  ],
  template: `
    <div class="space-y-8">
      <!-- IN-FLIGHT ─────────────────────────────────────────── -->
      @if (svc.inFlight().length > 0) {
        <section>
          <div class="mb-2">
            <h2 class="text-lg font-semibold">In progress</h2>
            <p class="text-sm text-[var(--text-secondary)]">
              These updates are running on your tenant. Status refreshes automatically.
            </p>
          </div>
          <div class="space-y-3">
            @for (upgrade of svc.inFlight(); track upgrade.id) {
              <es-card>
                <div class="flex items-center gap-4">
                  <es-spinner size="sm"></es-spinner>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <h3 class="font-medium">API {{ upgrade.apiVersion }}</h3>
                      <es-badge [label]="statusLabel(upgrade.status)" [variant]="statusVariant(upgrade.status)"></es-badge>
                    </div>
                    <p class="text-xs text-[var(--text-secondary)] font-mono mt-1">
                      schema {{ upgrade.fromSchemaVersion }} → {{ upgrade.toSchemaVersion }}
                    </p>
                    @if (upgrade.startedAt) {
                      <p class="text-xs text-[var(--text-secondary)] mt-1">
                        Started {{ upgrade.startedAt | date:'medium' }}
                      </p>
                    } @else if (upgrade.approvedAt) {
                      <p class="text-xs text-[var(--text-secondary)] mt-1">
                        Approved {{ upgrade.approvedAt | date:'medium' }}
                      </p>
                    }
                  </div>
                </div>
              </es-card>
            }
          </div>
        </section>
      }

      <!-- PENDING ─────────────────────────────────────────── -->
      <section>
        <div class="mb-2">
          <h2 class="text-lg font-semibold">Available updates</h2>
          <p class="text-sm text-[var(--text-secondary)]">
            Approve to start a pre-upgrade backup, then the schema migration.
          </p>
        </div>

        @if (svc.loading() && svc.all().length === 0) {
          <es-spinner label="Loading updates…"></es-spinner>
        } @else if (svc.loadError()) {
          <es-empty-state
            title="Couldn't load updates"
            description="We couldn't reach the update service. This is usually temporary."
            icon="⚠️"
            actionLabel="Try again"
            (action)="svc.loadAll()"
          ></es-empty-state>
        } @else if (svc.pending().length === 0) {
          <es-empty-state
            title="You're up to date"
            description="No pending schema updates for your organization."
            icon="✓"
          ></es-empty-state>
        } @else {
          <div class="space-y-3">
            @for (upgrade of svc.pending(); track upgrade.id) {
              <es-card>
                <div class="flex items-start gap-4">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <h3 class="font-medium">API {{ upgrade.apiVersion }}</h3>
                      <es-badge label="pending" variant="warning"></es-badge>
                    </div>
                    <p class="text-xs text-[var(--text-secondary)] font-mono mt-1">
                      schema {{ upgrade.fromSchemaVersion }} → {{ upgrade.toSchemaVersion }}
                    </p>
                    <p class="text-xs text-[var(--text-secondary)] mt-1">
                      Offered {{ upgrade.requestedAt | date:'medium' }}
                    </p>
                  </div>
                  <es-button
                    variant="primary"
                    size="sm"
                    [disabled]="svc.approving() !== null"
                    [loading]="svc.approving() === upgrade.id"
                    (clicked)="askApprove(upgrade)"
                  >Approve</es-button>
                </div>
              </es-card>
            }
          </div>
        }
      </section>

      <!-- HISTORY ─────────────────────────────────────────── -->
      @if (svc.history().length > 0) {
        <section>
          <button
            type="button"
            class="flex items-center gap-2 mb-2 text-left cursor-pointer"
            (click)="historyOpen.set(!historyOpen())"
          >
            <span class="text-sm text-[var(--text-secondary)]">{{ historyOpen() ? '▾' : '▸' }}</span>
            <h2 class="text-lg font-semibold">History ({{ svc.history().length }})</h2>
          </button>

          @if (historyOpen()) {
            <div class="space-y-2">
              @for (upgrade of svc.history(); track upgrade.id) {
                <es-card>
                  <div class="flex items-start gap-3">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 flex-wrap">
                        <p class="font-medium text-sm">API {{ upgrade.apiVersion }}</p>
                        <es-badge [label]="statusLabel(upgrade.status)" [variant]="statusVariant(upgrade.status)"></es-badge>
                      </div>
                      <p class="text-xs text-[var(--text-secondary)] font-mono mt-1">
                        schema {{ upgrade.fromSchemaVersion }} → {{ upgrade.toSchemaVersion }}
                      </p>
                      <p class="text-xs text-[var(--text-secondary)] mt-1">
                        @if (upgrade.completedAt) {
                          Completed {{ upgrade.completedAt | date:'medium' }}
                        } @else if (upgrade.startedAt) {
                          Started {{ upgrade.startedAt | date:'medium' }}
                        } @else {
                          Offered {{ upgrade.requestedAt | date:'medium' }}
                        }
                      </p>
                      @if (upgrade.status === 'FAILED' && upgrade.errorMessage) {
                        <pre class="mt-2 px-3 py-2 rounded bg-red-500/10 border border-red-500/30 text-xs text-red-300 whitespace-pre-wrap break-all">{{ upgrade.errorMessage }}</pre>
                      }
                    </div>
                  </div>
                </es-card>
              }
            </div>
          }
        </section>
      }

      <!-- CONFIRM ─────────────────────────────────────────── -->
      @if (toApprove(); as target) {
        <es-confirm-dialog
          [open]="true"
          title="Approve update?"
          [message]="approveMessage(target)"
          confirmLabel="Approve update"
          (confirmed)="confirmApprove(target)"
          (cancelled)="toApprove.set(null)"
        ></es-confirm-dialog>
      }
    </div>
  `,
})
export class UpgradesPanelComponent implements OnInit, OnDestroy {
  readonly svc = inject(UpgradeService);
  private readonly toast = inject(ToastService);

  readonly toApprove = signal<CustomerUpgrade | null>(null);
  readonly historyOpen = signal(false);

  /** Convenience exposure of the full list for tests + template typing. */
  readonly upgrades = computed(() => this.svc.all());

  ngOnInit(): void {
    this.svc.loadAll();
  }

  ngOnDestroy(): void {
    this.svc.stopPolling();
  }

  statusVariant(status: UpgradeStatus) {
    return STATUS_VARIANT[status];
  }

  statusLabel(status: UpgradeStatus): string {
    return STATUS_LABEL[status];
  }

  askApprove(upgrade: CustomerUpgrade): void {
    this.toApprove.set(upgrade);
  }

  approveMessage(upgrade: CustomerUpgrade): string {
    return `Approving "API ${upgrade.apiVersion}" will create a pre-upgrade backup in your `
      + `S3 bucket, then run the migration to schema ${upgrade.toSchemaVersion}. Customer routing `
      + `flips when the migration completes. This cannot be cancelled mid-run.`;
  }

  async confirmApprove(upgrade: CustomerUpgrade): Promise<void> {
    this.toApprove.set(null);
    try {
      await this.svc.approve(upgrade.id);
      this.toast.success(`Update ${upgrade.apiVersion} approved — running now`);
    } catch {
      this.toast.error('Failed to approve update');
    }
  }
}
